import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Plus, Search, MessageSquareOff, MessageSquare } from 'lucide-react';
import { formatarDocumento } from '@profitlens/dominio';
import { Cabecalho } from '../componentes/Cabecalho';
import { Carregando, Erro, Vazio } from '../componentes/Estados';
import { Reais, Texto, Dias } from '../componentes/Valor';
import { useAuth } from '../contextos/AuthContext';
import { useFornecedores } from '../servicos/fornecedores';

export function Fornecedores() {
  const { fornecedores, carregando, erro } = useFornecedores();
  const { podeEscrever } = useAuth();
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState<'TODOS' | 'ATIVOS' | 'INATIVOS'>('ATIVOS');

  const filtrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return fornecedores.filter((f) => {
      if (status === 'ATIVOS' && !f.ativo) return false;
      if (status === 'INATIVOS' && f.ativo) return false;
      if (!t) return true;
      return [f.razaoSocial, f.nomeFantasia, f.documento, f.contatoNome, f.email]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(t);
    });
  }, [fornecedores, busca, status]);

  const semConsentimento = fornecedores.filter((f) => f.ativo && !f.consentimento?.ativo).length;

  if (carregando) return <Carregando />;
  if (erro) return <Erro titulo="Não foi possível carregar os fornecedores" detalhe={erro} />;

  return (
    <div className="flex flex-col gap-5">
      <Cabecalho
        titulo="Fornecedores"
        descricao="De quem você compra, com as condições comerciais de cada um."
        acoes={
          podeEscrever && (
            <Link
              to="/fornecedores/novo"
              className="inline-flex items-center gap-1.5 rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90"
            >
              <Plus className="size-4" aria-hidden />
              Novo fornecedor
            </Link>
          )
        }
      />

      <div className="flex flex-wrap gap-2">
        <label className="relative min-w-56 flex-1">
          <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome, documento ou contato"
            className="w-full rounded border border-regua-2 bg-superficie py-1.5 pr-3 pl-8 text-sm outline-none focus:border-marca"
          />
        </label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          className="rounded border border-regua-2 bg-superficie px-3 py-1.5 text-sm outline-none focus:border-marca"
        >
          <option value="ATIVOS">Ativos</option>
          <option value="INATIVOS">Inativos</option>
          <option value="TODOS">Todos</option>
        </select>
      </div>

      {filtrados.length === 0 ? (
        <Vazio
          icone={<Building2 className="size-8" />}
          titulo={
            fornecedores.length === 0 ? 'Nenhum fornecedor cadastrado' : 'Nenhum resultado para este filtro'
          }
          descricao={
            fornecedores.length === 0
              ? 'Cadastre de quem você compra e vincule os produtos do portfólio dele. É o que permite comparar preço entre fornecedores.'
              : 'Ajuste a busca ou troque o filtro de situação.'
          }
          acao={
            fornecedores.length === 0 &&
            podeEscrever && (
              <Link
                to="/fornecedores/novo"
                className="rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90"
              >
                Cadastrar o primeiro
              </Link>
            )
          }
        />
      ) : (
        <>
          <div className="hidden overflow-x-auto rounded-md border border-regua-2 sm:block">
            <table className="w-full min-w-[48rem] border-collapse bg-superficie text-sm">
              <thead>
                <tr>
                  {['Fornecedor', 'CPF/CNPJ', 'Contato', 'Prazo', 'Pedido mínimo', 'Comunicação'].map((h) => (
                    <th
                      key={h}
                      className="border-b border-regua-2 px-3 py-2 text-left font-mono text-[0.62rem] font-semibold tracking-wider text-marca uppercase"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtrados.map((f) => (
                  <tr key={f.id} className="border-b border-regua last:border-0 even:bg-marca/4">
                    <td className="px-3 py-2">
                      <Link to={`/fornecedores/${f.id}`} className="font-medium hover:text-marca">
                        {f.nomeFantasia || f.razaoSocial}
                      </Link>
                      {f.nomeFantasia && (
                        <div className="text-[0.7rem] text-tinta-3">{f.razaoSocial}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-tinta-2">
                      {f.documento ? formatarDocumento(f.documento) : <span className="text-tinta-3 italic">n/i</span>}
                    </td>
                    <td className="px-3 py-2 text-tinta-2">
                      <Texto valor={f.contatoNome} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Dias dias={f.prazoMedioEntregaDias} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Reais cents={f.pedidoMinimoCents} />
                    </td>
                    <td className="px-3 py-2">
                      {f.consentimento?.ativo ? (
                        <span className="inline-flex items-center gap-1 text-xs text-c-via">
                          <MessageSquare className="size-3.5" aria-hidden />
                          autorizada
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 text-xs text-tinta-3"
                          title="O Hermes não envia mensagem sem consentimento. O registro manual continua permitido."
                        >
                          <MessageSquareOff className="size-3.5" aria-hidden />
                          sem consentimento
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="flex flex-col gap-2 sm:hidden">
            {filtrados.map((f) => (
              <li key={f.id} className="rounded-md border border-regua-2 bg-superficie p-3">
                <Link to={`/fornecedores/${f.id}`} className="flex flex-col gap-1">
                  <span className="font-medium">{f.nomeFantasia || f.razaoSocial}</span>
                  <span className="font-mono text-xs text-tinta-3">
                    {f.documento ? formatarDocumento(f.documento) : 'sem documento'}
                  </span>
                  <span className="text-xs text-tinta-2">
                    <Texto valor={f.contatoNome} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-3 text-xs text-tinta-3">
            <span>
              <span className="num">{filtrados.length}</span> de{' '}
              <span className="num">{fornecedores.length}</span>{' '}
              {fornecedores.length === 1 ? 'fornecedor' : 'fornecedores'}
            </span>
            {semConsentimento > 0 && (
              <span className="text-c-res">
                {semConsentimento} sem consentimento para comunicação
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
